import { Color, Group, Matrix, Path, Point, Project, } from "paper/dist/paper-core";
import { PaperUtils } from "../utilities/paper-utils";
import { defaultEntityPathSettings, defaultGridLineSettings, defaultGridTextSettings, defaultReferenceLineSettings, defaultTextSettings, pixelAttenuationOffset, } from "../settings/altitude-view-settings";
import { AssetType } from "../types";
export class LineSectionView {
    _project;
    _view;
    _canvas;
    _zoomFactor = 1.05;
    // to keep all grid lines together and easy to delete all grid lines at same time.
    _gridGroup;
    _gridSizeInPixel = 50;
    _outlineRenderGroup;
    _gridText;
    isDragging = false;
    startPoint;
    _currentEntityID;
    _presentation;
    _referenceLine;
    _entityRenderGroup;
    _textRenderGroup;
    _heightTextRenderGroup;
    _lastSectionArgs;
    get currentEntityID() {
        return this._currentEntityID;
    }
    constructor(presentation) {
        this._presentation = presentation;
        this._canvas = document.createElement("canvas");
        // Setup canvas here
        this._canvas.style.width = "100%";
        this._canvas.style.height = "100%";
        this._canvas.style.backgroundColor = "#566573";
        this._project = new Project(this._canvas);
        this._project.activate();
        this._view = this._project.view;
        this._view.transform(new Matrix(1, 0, 0, -1, 0, 0));
        //to keep all grid lines in one group
        this._gridGroup = new Group();
        this._gridText = new Group();
        this._entityRenderGroup = new Group();
        this._textRenderGroup = new Group();
        this._heightTextRenderGroup = new Group();
        this._textRenderGroup.addChild(this._heightTextRenderGroup);
        //Create grid lines
        this.changeGridSize(this._gridSizeInPixel);
        this._canvas.addEventListener("wheel", (event) => {
            PaperUtils.zoom(event, this._zoomFactor, this._view);
            this.changeGridSize(this._gridSizeInPixel);
            this._attenuate();
        });
        this._canvas.addEventListener("mousedown", (event) => {
            this.isDragging = true;
            this.startPoint = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
        });
        this._canvas.addEventListener("mousemove", (event) => {
            if (this.isDragging) {
                const point = PaperUtils.getCoordinateOnView(this._view, event.pageX, event.pageY);
                this._view.translate(point.subtract(this.startPoint));
                this.changeGridSize(this._gridSizeInPixel);
                this._view.update();
            }
        });
        this._canvas.addEventListener("mouseup", () => {
            this.isDragging = false;
        });
    }
    _activateView() {
        this._project.activate();
    }
    _attenuate() {
        //attenuate Entities
        this._outlineRenderGroup &&
            this._outlineRenderGroup.children.forEach((l) => PaperUtils.attenuateLines(l, this._view, defaultEntityPathSettings.defaultPixelSize));
        //attenuate Grids
        this._gridGroup &&
            this._gridGroup.children.forEach((line) => PaperUtils.attenuateLines(line, this._view, defaultGridLineSettings.defaultPixelSize));
        this._gridText &&
            this._gridText.children.forEach((text) => {
                PaperUtils.attenuateEntites(text, this._view, defaultGridTextSettings.defaultPixelSize);
            });
        this._entityRenderGroup &&
            this._entityRenderGroup?.children.forEach((l) => PaperUtils.attenuateLines(l, this._view, defaultEntityPathSettings.defaultPixelSize));
        this._heightTextRenderGroup.children.forEach((text) => {
            PaperUtils.attenuateEntites(text, this._view, defaultTextSettings.defaultPixelSize);
            PaperUtils.setAttenuatedPos(text, pixelAttenuationOffset.heightTextOffsetAltitudeView, this._view);
        });
        this._view.requestUpdate();
    }
    clearView() {
        if (this._referenceLine) {
            this._referenceLine.remove();
            this._referenceLine = undefined;
        }
        if (this._outlineRenderGroup) {
            this._outlineRenderGroup.removeChildren();
            this._outlineRenderGroup = undefined;
        }
        this._heightTextRenderGroup.removeChildren();
        //remove grid text
        this._gridText.removeChildren();
    }
    _createReferenceLine() {
        this._referenceLine = PaperUtils.createLine(new Point(0, 10000), new Point(0, -10000), defaultReferenceLineSettings.strokeColor);
        this._referenceLine.scale(0.1);
        this._referenceLine.data.name = "secViewPlane";
        this._entityRenderGroup.addChild(this._referenceLine);
    }
    resizeView(size) {
        if (size.height && size.width) {
            this._project.view.viewSize.height = size.height;
            this._project.view.viewSize.width = size.width;
            this._view.update();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    _createPath(pointArr, color) {
        const path = new Path();
        path.strokeColor = new Color(color);
        pointArr.forEach((arr) => {
            path.add(new Point(arr[0], arr[1]));
        });
        return path;
    }
    renderHeightText(referencePoint, heightTextString) {
        const heightText = PaperUtils.createText(referencePoint, defaultTextSettings.fillColor, heightTextString, 1);
        heightText.data.moveDir = new Point(0, 1);
        heightText.data.refPosition = heightText.position.clone();
        this._heightTextRenderGroup.addChild(heightText);
    }
    renderData(pointID, data, fit) {
        this._activateView();
        this.clearView();
        if (pointID === undefined || data === undefined) {
            return;
        }
        this._lastSectionArgs = { pointID, data };
        this._currentEntityID = pointID;
        const outlineData = data;
        const rect = this._canvas.getBoundingClientRect();
        this.resizeView({ width: rect.width, height: rect.height });
        const getColorOfAssetType = (assetType) => {
            switch (assetType) {
                case AssetType.Point_Cloud:
                    return "#00FF00";
                case AssetType.Design_Data:
                    return "#FF00FF";
                case AssetType.Road_Data:
                case AssetType.Flat_Ground_Data:
                case AssetType.Trench_Data:
                    return "#00FFFF";
                case AssetType.Spreading_Compaction_Data:
                    return "#FF8A65";
            }
        };
        this._outlineRenderGroup = new Group();
        outlineData.leftOutline.outline2D.forEach((outline2d, assetType) => {
            const color = getColorOfAssetType(assetType);
            outline2d.forEach((outlineArr) => {
                outlineArr.forEach((outline) => {
                    this._outlineRenderGroup.addChild(this._createPath(outline, color));
                });
            });
        });
        outlineData.rightOutline.outline2D.forEach((outline2d, assetType) => {
            const color = getColorOfAssetType(assetType);
            outline2d.forEach((outlineArr) => {
                outlineArr.forEach((outline) => {
                    this._outlineRenderGroup.addChild(this._createPath(outline, color));
                });
            });
        });
        if (fit) {
            //fit the entities in current view
            const bounds = this._outlineRenderGroup.bounds;
            PaperUtils.fitView(this._view, bounds.center, bounds.width, bounds.height);
        }
        this._createReferenceLine();
        const referenceHeightText = this._presentation.formatFixed(outlineData.outlineHeightAtReference, 3) +
            this._presentation.getUnitLabel();
        const outlineHeightPoint = new Point(outlineData.referencePointHeight[0][0], outlineData.outlineHeightAtReference);
        this.renderHeightText(outlineHeightPoint, referenceHeightText);
        this.changeGridSize(this._gridSizeInPixel);
        this._attenuate();
    }
    setBackgroundColor(color) {
        const tempColor = color ? color : "#566573";
        this._canvas.style.backgroundColor = tempColor;
    }
    updatePresentation(presentation) {
        this._presentation = presentation;
        if (this._lastSectionArgs) {
            this.renderData(this._lastSectionArgs.pointID, this._lastSectionArgs.data, false);
        }
        else {
            this._activateView();
            this.changeGridSize(this._gridSizeInPixel);
        }
    }
    changeGridSize(step) {
        this._activateView();
        PaperUtils.createGrid(this._view, step, this._gridGroup, this._gridText, this._presentation);
        this._attenuate();
    }
    getCanvas() {
        return this._canvas;
    }
}
//# sourceMappingURL=view-2d-line-section.js.map